import { Logger } from './../../logger/Logger';
import _ from 'lodash';
import {
  FuidRrnInfo,
  CollectionScope,
  AnnotationThread,
  CommentId,
  RepoItem,
  ShapeId,
  AnnotationLocation,
  SpatialLocation,
  TemporalLocation,
  GetDrawingAnnotationsMessage,
  GetDrawingAnnotationsQuery,
  GetDrawingAnnotationsResponse,
  DrawingAnnotation,
  DeleteDrawingAnnotationsQuery,
  DeleteDrawingAnnotationsMessage,
  DeleteDrawingAnnotationsResponse,
  ClearAllDrawingAnnotationsQuery,
  ClearAllDrawingAnnotationsMessage,
  ClearAllDrawingAnnotationsResponse,
  SaveDrawingAnnotationsQuery,
  SaveDrawingAnnotationsMessage,
  SaveDrawingAnnotationsResponse
} from './../../autogenerated/ZoomBridge';

import FileInfoUtil from './../../util/FileInfoUtil';
import ErrorId from './../../protocol/ErrorId';

export default class DrawingUtil {

  /**
   *
   * @param {number} collectionId
   * @param {FuidRrnInfo} fuidRrnInfo
   * @param {number} lastReceivedRrn
   * @param {Function.<GetDrawingAnnotationsResponse,ErrorId>} onResult
   * @param {number} shapeId Optional. Pass if you want to get specific space.
   */
  static getDrawings(collectionId, fuidRrnInfo, lastReceivedRrn, onResult, shapeId = undefined) {
    const message = new GetDrawingAnnotationsMessage()
      .setCollectionScope(new CollectionScope().setCollectionId(collectionId))
      .setFuidRrnInfo(fuidRrnInfo)
      .setShapeId(shapeId)
      .setLastReceivedRrn(lastReceivedRrn);
    const query = new GetDrawingAnnotationsQuery();
    query.message = message;
    query.run().then(function (response) {
      /**
       * @type {GetDrawingAnnotationsResponse}
       */
      const getDrawingAnnotationsResponse = response;
      onResult(getDrawingAnnotationsResponse, null);
    }, function (errorId) {
      onResult(null, errorId);
    });
  }


  /**
   * Retursn the array of drawings from GetDrawingAnnotationsResponse.
   * @param {GetDrawingAnnotationsResponse} getDrawingAnnotationsResponse
   * @returns {Array.<DrawingAnnotation>}
   */
  static getShapes(getDrawingAnnotationsResponse) {
    if (getDrawingAnnotationsResponse) {
      const shapes = getDrawingAnnotationsResponse.getShapes();
      return shapes;
    }
    return null;
  }

  /**
   * Returns the number of shapes present in GetDrawingAnnotationsResponse
   * @param {GetDrawingAnnotationsResponse} getDrawingAnnotationsResponse
   * @returns {number} Number of shapes
   */
  static getShapesCount(getDrawingAnnotationsResponse) {
    const shapes = DrawingUtil.getShapesCount(getDrawingAnnotationsResponse);
    return _.size(shapes);
  }

  /**
   * Returns the Array of shape ids which are deleted.
   * @param {GetDrawingAnnotationsResponse} getDrawingAnnotationsResponse
   * @returns {Array.<number>} Array of shape ids.
   */
  static getDeletedShapes(getDrawingAnnotationsResponse) {
    if (getDrawingAnnotationsResponse) {
      return getDrawingAnnotationsResponse.getDeletedShapes();
    }
    return null;
  }

  /**
   * Returns the count of deleted shapes in response
   * @param {GetDrawingAnnotationsResponse} getDrawingAnnotationsResponse
   * @returns {number}
   */
  static getDeletedShapesCount(getDrawingAnnotationsResponse) {
    return _.size(DrawingUtil.getDeletedShapes(getDrawingAnnotationsResponse));
  }

  /**
   * For Images, get the temporal location.
   * @param {DrawingAnnotation} drawingAnnotation
   * @returns {SpatialLocation} For images/multipage, SpatialLocation can be used.
   */
  static getSpatialLocation(drawingAnnotation, createNewIfNotExist = false) {
    if (drawingAnnotation) {
      /**
       * @type {AnnotationLocation}
       */
      let annotationLocation = drawingAnnotation.getAnnotationLocation();
      if (createNewIfNotExist && !annotationLocation) {
        annotationLocation = new AnnotationLocation();
        drawingAnnotation.setAnnotationLocation(annotationLocation);
      }
      if (annotationLocation) {
        /**
         * @type {TemporalLocation}
         */
        let spatialLocation = annotationLocation.getSpatialLocation();
        if (createNewIfNotExist && !spatialLocation) {
          spatialLocation = new SpatialLocation();
          annotationLocation.setSpatialLocation(spatialLocation);
        }
        return spatialLocation;
      }
    }
    return null;
  }

  /**
 * For video/animation/page number, get the temporal location.
 * @param {DrawingAnnotation} drawingAnnotation
 * @returns {TemporalLocation} If it is video, then TemporalLocation can be used to get the offset at which drawing is added/made.
 */
  static getTemporalLocation(drawingAnnotation, createNewIfNotExist = false) {
    if (drawingAnnotation) {
      /**
       * @type {AnnotationLocation}
       */
      let annotationLocation = drawingAnnotation.getAnnotationLocation();
      if (createNewIfNotExist && !annotationLocation) {
        annotationLocation = new AnnotationLocation();
        drawingAnnotation.setAnnotationLocation(annotationLocation);
      }
      if (annotationLocation) {
        /**
         * @type {TemporalLocation}
         */
        let temporalLocation = annotationLocation.getTemporalLocation();
        if (createNewIfNotExist && !temporalLocation) {
          temporalLocation = new TemporalLocation();
          annotationLocation.setTemporalLocation(temporalLocation);
        }
        return temporalLocation;
      }
    }
    return null;
  }


  /**
   * @param {DrawingAnnotation} drawingAnnotation
   * @return {number} Time in milliseconds at which thread is created.
   */
  static getVideOffsetInMilliseconds(drawingAnnotation) {
    /**
     * @type {TemporalLocation}
     */
    const temporalLocation = DrawingUtil.getTemporalLocation(drawingAnnotation);
    if (temporalLocation) {
      return temporalLocation.getTimeOffset();
    }
    return 0;
  }

  /**
   * @param {DrawingAnnotation} drawingAnnotation
   * @returns {number}
   */
  static getPageNumber(drawingAnnotation) {
    /**
     * @type {TemporalLocation}
     */
    const temporalLocation = DrawingUtil.getTemporalLocation(drawingAnnotation);
    if (temporalLocation) {
      return temporalLocation.getFrameNumber();
    }
    return 0;
  }


  /**
   * Width offset at which spot is added.
   * @param {DrawingAnnotation} drawingAnnotation
   */
  static getWidthOffset(drawingAnnotation) {
    const spatialLocation = DrawingUtil.getSpatialLocation(drawingAnnotation);
    if (spatialLocation) {
      return spatialLocation.getWidthOffset();
    }
    return 0;
  }

  /**
   * Height offset at which commet is added.
   * @param {DrawingAnnotation} drawingAnnotation
   */
  static getHeightOffset(drawingAnnotation) {
    const spatialLocation = DrawingUtil.getSpatialLocation(drawingAnnotation);
    if (spatialLocation) {
      return spatialLocation.getHeightOffset();
    }
    return 0;
  }

  /**
   * Height of asset at whcih spot is added.
   * @param {DrawingAnnotation} drawingAnnotation
   */
  static getHeight(drawingAnnotation) {
    /**
     * @type {SpatialLocation}
     */
    const spatialLocation = DrawingUtil.getSpatialLocation(drawingAnnotation);
    if (spatialLocation) {
      return spatialLocation.getHeight();
    }
    return 0;
  }

  /**
   * Width of asset at whcih spot is added.
   * @param {DrawingAnnotation} drawingAnnotation
   */
  static getWidth(drawingAnnotation) {
    /**
     * @type {SpatialLocation}
     */
    const spatialLocation = DrawingUtil.getSpatialLocation(drawingAnnotation);
    if (spatialLocation) {
      return spatialLocation.getWidth();
    }
    return 0;
  }

  /**
   *
   * @param {DrawingAnnotation} svg
   */
  static createSvgDrawing(svg) {
    const drawingAnnotation = new DrawingAnnotation();
    drawingAnnotation.setData(svg);
    return drawingAnnotation;
  }

  static setHeightWidthOffset(drawingAnnotation, heightOffset, widthOffset, height = 0, width = 0) {
    const spatialLocation = DrawingUtil.getSpatialLocation(drawingAnnotation, true);
    spatialLocation.setHeightOffset(height).setWidthOffset(width).setHeight(height).setWidth(width);
  }

  static setTimeInMilliSeconds(drawingAnnotation, timeInMilliSeconds) {
    const temporalLocation = DrawingUtil.getTemporalLocation(drawingAnnotation, true);
    temporalLocation.setTimeOffset(timeInMilliSeconds);
  }

  static setPageNumber(drawingAnnotation, pageNumber) {
    const temporalLocation = DrawingUtil.getTemporalLocation(drawingAnnotation, true);
    temporalLocation.setFrameNumber(pageNumber);
  }


  /**
   *
   * @param {number} collectionId
   * @param {FuidRrnInfo} fuidRrnInfo
   * @param {DrawingAnnotation} shape
   * @param {Function.<SaveDrawingAnnotationsResponse, ErrorId>} onResult
   */
  static saveDrawing(collectionId, fuidRrnInfo, shape, onResult) {
    const message = new SaveDrawingAnnotationsMessage();
    message.setCollectionScope(new CollectionScope().setCollectionId(collectionId));
    message.setFuidRrnInfo(fuidRrnInfo);
    message.setShapes([shape]);
    const query = new SaveDrawingAnnotationsQuery();
    query.message = message;
    query.run().then(function (response) {
      onResult(response);
    }, function (errorId) {
      onResult(null, errorId);
    });
  }

  /**
   *
   * @param {number} shapeId
   * @param {Function.<number, ErrorId>} onResult If successful then first param value would be greater than zero.
   */
  static deleteDrawing(shapeId, onResult) {
    DrawingUtil.deleteDrawings([shapeId], onResult);
  }

  /**
   *
   * @param {number} shapeId
   * @returns {ShapeId} Returns the ShapeId Object.
   */
  static toShapeIdObject(shapeId) {
    return new ShapeId().setShapeId(shapeId);
  }

  /**
   *
   * @param {Array.<number>} shapeIds
   * @param {Function.<number, ErrorId>} onResult If successful then first param value would be greater than zero.
   */
  static deleteDrawings(shapeIds, onResult) {
    const shapeIdsObjects = _.map(shapeIds, DrawingUtil.toShapeIdObject);
    const message = new DeleteDrawingAnnotationsMessage().setDeletedShapes(shapeIdsObjects);
    const query = new DeleteDrawingAnnotationsQuery();
    query.message = message;
    query.run().then(
      function (response) {
        /**
         * @type {DeleteDrawingAnnotationsResponse}
         */
        const deleteDrawingAnnotationsResponse = response;
        const lastReceivedRrn = deleteDrawingAnnotationsResponse.getLastReceivedRrn();
        onResult(lastReceivedRrn, undefined);
      },
      function (errorId) {
        onResult(undefined, errorId);
      }
    );
  }


  /**
   * Deletes all the drawings for given params from Zoom server.
   * @param  {number} collectionId
   * @param  {FuidRrnInfo} fuidRrnInfo
   * @param {AnnotationLocation} annotationLocation Optional: Deletes all the drawings for given location.
   * @param {Function.<number, ErrorId>} onResult If successful then first param value would be greater than zero.
   */
  static clearAllDrawings(collectionId, fuidRrnInfo, annotationLocation = null) {
    const message = new ClearAllDrawingAnnotationsMessage()
      .setAnnotationLocation(annotationLocation)
      .setCollectionScope(new CollectionScope().setCollectionId(collectionId))
      .setFuidRrnInfo(fuidRrnInfo);
    const query = new ClearAllDrawingAnnotationsQuery();
    query.message = message;
    return query.run().then(
      function (response) {
        /**
         * @type {ClearAllDrawingAnnotationsResponse}
         */
        const clearAllDrawingAnnotationsResponse = response;
        const rrn = clearAllDrawingAnnotationsResponse.getLastReceivedRrn();
        return rrn;
      });
  }


  static testDrawing() {
    const svg = "<svg>ABC</svg>";
    const fuidRrnInfo = new FuidRrnInfo().setBid(1).setRrn(1).setFuid(1);
    const collectionId = 6;
    const drawing = DrawingUtil.createSvgDrawing(svg);
    DrawingUtil.saveDrawing(
      collectionId,
      fuidRrnInfo,
      drawing,
      (result) => {
        console.log('saveDrawingResult', result);

        DrawingUtil.getDrawings(
          6,
          fuidRrnInfo,
          -1, // rrn
          (getDrawings) => {
            console.log(getDrawings.getShapes()); // console.log returns empty array here
          });
      } // console.log shows result === undefined here
    );

    //Here's how I'm trying to retrieve drawings:

  }

}